import { Controller } from "@hotwired/stimulus"

// A cooking timer the user controls: shows mm:ss, ± 30s to adjust, Start/Reset
// to run it. The duration is seeded per step by the cooking-mode controller —
// the time detected in the step text ("boil 2 minutes" → 2:00), or a 5:00
// default when the step names no time — and the cook can nudge it from there.
export default class extends Controller {
  static targets = ["display", "button"]
  static values = { seconds: { type: Number, default: 300 } }

  connect() {
    this.running = false
    this.renderIdle()
  }

  // Seeded by cooking-mode on each step. A running timer is left alone — the
  // cook may have started it and moved on to read ahead.
  setDuration(seconds) {
    if (this.running) return
    this.secondsValue = Math.max(30, seconds || 300)
    this.renderIdle()
  }

  adjust(event) {
    if (this.running) return
    const delta = parseInt(event.currentTarget.dataset.delta, 10)
    if (isNaN(delta)) return
    this.secondsValue = Math.min(7200, Math.max(30, this.secondsValue + delta))
    this.renderIdle()
  }

  toggle() {
    this.running ? this.reset() : this.start()
  }

  // "Timer 3 minutes" in hands-free Cook Mode: a fresh countdown of that
  // length, replacing one already running.
  runFor(seconds) {
    this.reset()
    this.secondsValue = Math.min(7200, Math.max(5, seconds))
    this.start()
  }

  start() {
    this.remaining = this.secondsValue
    this.running = true
    this.tick()
    this.interval = setInterval(() => this.tick(), 1000)
    if (this.hasButtonTarget) this.buttonTarget.textContent = "Reset"
  }

  reset() {
    clearInterval(this.interval)
    clearTimeout(this.resetTimeout)
    this.running = false
    if (this.hasButtonTarget) this.buttonTarget.textContent = "Start"
    this.renderIdle()
  }

  tick() {
    if (this.remaining <= 0) {
      this.finish()
      return
    }
    this.displayTarget.textContent = this.format(this.remaining)
    this.remaining--
  }

  finish() {
    clearInterval(this.interval)
    this.running = false
    this.displayTarget.textContent = "Done!"
    if (this.hasButtonTarget) this.buttonTarget.textContent = "Start"

    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Timer done!", { body: "Your cooking timer has finished." })
    }

    this.dispatch("finished")

    this.resetTimeout = setTimeout(() => { if (!this.running) this.renderIdle() }, 3000)
  }

  renderIdle() {
    this.displayTarget.textContent = this.format(this.secondsValue)
  }

  format(total) {
    const m = Math.floor(total / 60)
    const s = total % 60
    return `${m}:${s.toString().padStart(2, "0")}`
  }

  disconnect() {
    clearInterval(this.interval)
    clearTimeout(this.resetTimeout)
  }
}
